export const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
] as const;

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const brlCompact = new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', maximumFractionDigits: 0,
});

/** Centavos → "R$ 1.234,56". */
export const formatBRL = (cents: number): string => brl.format((cents || 0) / 100);

const money = new Map<string, Intl.NumberFormat>([['BRL', brl]]);

/** Centavos na moeda deles → "R$ 37,00", "US$ 37,00". Moeda que não vier certa vira real. */
export function formatMoney(cents: number, currency = 'BRL'): string {
  const code = /^[A-Z]{3}$/.test(currency) ? currency : 'BRL';
  let format = money.get(code);
  if (!format) {
    format = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code });
    money.set(code, format);
  }
  return format.format((cents || 0) / 100);
}

/** Centavos → "R$ 1.235" (sem centavos), para números grandes em painéis. */
export const formatBRLShort = (cents: number): string => brlCompact.format((cents || 0) / 100);

/** Centavos → "R$ 1,2 mi" / "R$ 45,3 mil" — usado em eixos de gráfico. */
export function formatBRLCompact(cents: number): string {
  const value = (cents || 0) / 100;
  if (Math.abs(value) >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1).replace('.', ',')} mi`;
  if (Math.abs(value) >= 1_000) return `R$ ${(value / 1_000).toFixed(0)} mil`;
  return brlCompact.format(value);
}

export const formatNumber = (value: number): string => new Intl.NumberFormat('pt-BR').format(value || 0);

export const formatPercent = (fraction: number, digits = 0): string =>
  `${((fraction || 0) * 100).toFixed(digits).replace('.', ',')}%`;

/** Reais digitados ("1.234,56", "1234.56", 1234.56) → centavos inteiros. */
export function toCents(value: string | number): number {
  if (typeof value === 'number') return Math.round(value * 100);
  const cleaned = value
    .trim()
    .replace(/[R$\s ]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

const inputNumber = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Centavos → "1.234,56" para preencher um input de texto (o `toCents` lê de volta). */
export const centsToInput = (cents: number): string => (cents ? inputNumber.format(cents / 100) : '');

/** "2026-01-10" → "10/01/2026". */
export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : '—';
};

export const formatDateTime = (date: string, time?: string): string =>
  time ? `${formatDate(date)} às ${time}` : formatDate(date);

/** ISO completo → "10/01/2026 14:30" (mantém o horário wall-clock). */
export const formatISODateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return `${formatDate(iso.slice(0, 10))} ${iso.slice(11, 16)}`;
};

export const monthLabelFromDate = (iso: string): string => {
  const index = Number(iso.slice(5, 7)) - 1;
  return MESES[index] ?? MESES[0];
};

/** Um intervalo wall-clock por extenso: o mês inteiro pelo nome ("setembro de 2026"); o resto, de data a data. */
export function formatRange(fromIso: string, toIso: string): string {
  const [year, month, day] = fromIso.slice(0, 10).split('-');
  const lastDay = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
  const wholeMonth = day === '01' && toIso.slice(0, 7) === fromIso.slice(0, 7) && Number(toIso.slice(8, 10)) === lastDay;
  return wholeMonth ? `${monthLabelFromDate(fromIso).toLowerCase()} de ${year}` : `${formatDate(fromIso)} a ${formatDate(toIso)}`;
}

/** Hoje no formato AAAA-MM-DD, no fuso do navegador. */
export function todayISO(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

/** Agora no formato HH:MM, no fuso do navegador. */
export function nowHHMM(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

/** Iniciais para avatar: "Rafael Moura" → "RM". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrador',
  MEMBER: 'Membro',
};

export const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Em andamento',
  WON: 'Convertido',
  LOST: 'Perdido',
};

/** Tipo do comprovante, curto — nos selos e nas tabelas. */
export const LEVERAGE_LABELS: Record<string, string> = {
  SALE: 'Venda',
  DEPOSIT: 'Depósito',
  WELCOME: 'Alav. boas-vindas',
  EVENT: 'Alav. evento',
  LIVE: 'Repasse Live',
  GAS: 'Gás',
  UNA_DEPOSIT: 'Depósito UNA',
};

/** Tipo do comprovante por extenso — no formulário e nos filtros. */
export const LEVERAGE_LONG_LABELS: Record<string, string> = {
  SALE: 'Venda',
  // Sem dizer se foi de boas-vindas ou de evento: fica pendente até alguém dizer.
  DEPOSIT: 'Depósito sem tipo',
  WELCOME: 'Depósito · Alavancagem boas-vindas',
  EVENT: 'Depósito · Alavancagem evento',
  LIVE: 'Repasse Live',
  GAS: 'Gás',
  UNA_DEPOSIT: 'Depósito UNA Markets',
};

/** Situação da alavancagem. */
export const LEVERAGE_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  DONE: 'Realizada',
  FAILED: 'Não realizada',
};

/** Situação do repasse. */
export const TRANSFER_LABELS: Record<string, string> = {
  NONE: 'Sem repasse',
  PENDING: 'Pendente',
  DONE: 'Feito',
};

export const PERIOD_LABELS: Record<string, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  week: 'Últimos 7 dias',
  month: 'Este mês',
  lastMonth: 'Mês passado',
  quarter: 'Trimestre',
  year: 'Este ano',
  all: 'Todo o período',
  custom: 'Personalizado',
};

/** Duração em segundos, como se fala: "45 s", "12 min", "1 h 20 min", "2 d 3 h". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const minutes = Math.round(s / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days} d ${restHours} h` : `${days} d`;
}

/** Percentual que já vem de 0 a 100 (as taxas do atendimento). */
export const formatRate = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(value)}%`;

export const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  email: 'E-mail',
  instagram: 'Instagram',
  support_chat: 'Chat de suporte',
  web_widget: 'Widget do site',
};

/** Situação da conversa na For All Sales. */
export const CONVERSATION_STATUS_LABELS: Record<string, string> = {
  open: 'Aberta',
  pending: 'Pendente',
  resolved: 'Resolvida',
};

export const APPOINTMENT_LABELS: Record<string, string> = {
  scheduled: 'Marcadas',
  completed: 'Realizadas',
  cancelled: 'Canceladas',
  rescheduled: 'Remarcadas',
};

/** Como o lead chegou à pessoa. */
export const ASSIGNMENT_METHOD_LABELS: Record<string, string> = {
  round_robin: 'Rodízio',
  load_balanced: 'Por carga',
  manual_auto: 'Automática',
  manual_reassign: 'Transferência',
  direct: 'Direta',
  sticky_contact: 'Já era dele',
  first_come: 'Primeiro que pegou',
};

/** Papel e função na conta da For All Sales. */
export const FAS_ROLE_LABELS: Record<string, string> = {
  owner: 'Dono',
  co_owner: 'Administrador',
  admin: 'Admin',
  member: 'Membro',
  gestor: 'Gestor',
  analista: 'Analista',
};
